import { escapeMarkdownLinkText, normalizeInternalLinkValue, parseInternalHref, } from "./internal-link-token.js";
export const MESSAGE_MENTION_PATH = "/__df_mention__";
export const DEFAULT_MESSAGE_MENTION_SUGGESTION_CHAR = "@";
const MENTION_VERSION_PARAM = "version";
const MENTION_DIFF_PARAM = "diff";
function parsePositiveInteger(value) {
    if (!value || !/^\d+$/.test(value))
        return null;
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}
function normalizeVersionIntent(intent) {
    if (!intent)
        return undefined;
    if (intent.kind === "preview") {
        return Number.isSafeInteger(intent.versionNumber) && intent.versionNumber > 0
            ? { kind: "preview", versionNumber: intent.versionNumber }
            : undefined;
    }
    const from = intent.fromVersionNumber;
    const to = intent.toVersionNumber;
    if (!Number.isSafeInteger(from) || !Number.isSafeInteger(to) || from <= 0 || to <= 0) {
        return undefined;
    }
    return {
        kind: "diff",
        fromVersionNumber: Math.min(from, to),
        toVersionNumber: Math.max(from, to),
    };
}
/**
 * Compact, attribute-safe spelling of a facet — the same suffix grammar the entity routes use
 * (`ver/12`, `diff/11-12`) — for DOM data attributes and editor node attrs.
 */
export function serializeMentionVersionRef(intent) {
    const normalized = normalizeVersionIntent(intent);
    if (!normalized)
        return null;
    return normalized.kind === "preview"
        ? `ver/${normalized.versionNumber}`
        : `diff/${normalized.fromVersionNumber}-${normalized.toVersionNumber}`;
}
export function parseMentionVersionRef(value) {
    if (!value)
        return undefined;
    const trimmed = value.trim();
    const preview = /^ver\/(\d+)$/.exec(trimmed);
    if (preview) {
        const versionNumber = parsePositiveInteger(preview[1]);
        return versionNumber ? { kind: "preview", versionNumber } : undefined;
    }
    const diff = /^diff\/(\d+)-(\d+)$/.exec(trimmed);
    if (diff) {
        const from = parsePositiveInteger(diff[1]);
        const to = parsePositiveInteger(diff[2]);
        return from && to
            ? normalizeVersionIntent({ kind: "diff", fromVersionNumber: from, toVersionNumber: to })
            : undefined;
    }
    return undefined;
}
function toMentionLabel(label, fallbackId) {
    const normalizedLabel = normalizeInternalLinkValue(label);
    const normalizedFallbackId = normalizeInternalLinkValue(fallbackId);
    const value = normalizedLabel || normalizedFallbackId;
    if (!value)
        return "@mention";
    return value.startsWith("@") ? value : `@${value}`;
}
export function buildMessageMentionHref(reference) {
    const id = normalizeInternalLinkValue(reference.id);
    const mentionType = normalizeInternalLinkValue(reference.mentionType);
    if (!id || !mentionType)
        return null;
    const params = new URLSearchParams({ id, type: mentionType });
    const versionIntent = normalizeVersionIntent(reference.versionIntent);
    if (versionIntent?.kind === "preview") {
        params.set(MENTION_VERSION_PARAM, String(versionIntent.versionNumber));
    }
    else if (versionIntent?.kind === "diff") {
        params.set(MENTION_DIFF_PARAM, `${versionIntent.fromVersionNumber}-${versionIntent.toVersionNumber}`);
    }
    return `${MESSAGE_MENTION_PATH}?${params.toString()}`;
}
export function serializeMessageMentionToken(token) {
    const label = toMentionLabel(token.label, token.id);
    const href = buildMessageMentionHref(token);
    if (!href)
        return label;
    return `[${escapeMarkdownLinkText(label)}](${href})`;
}
export function buildMessageMentionNodeAttrs(token) {
    return {
        id: normalizeInternalLinkValue(token.id),
        label: normalizeInternalLinkValue(token.label),
        mentionType: normalizeInternalLinkValue(token.mentionType),
        mentionSuggestionChar: DEFAULT_MESSAGE_MENTION_SUGGESTION_CHAR,
        versionRef: serializeMentionVersionRef(token.versionIntent),
    };
}
/** Inverse of {@link buildMessageMentionNodeAttrs} for a node's persisted attrs. */
export function messageMentionReferenceFromNodeAttrs(attrs) {
    const id = normalizeInternalLinkValue(typeof attrs.id === "string" ? attrs.id : "");
    const mentionType = normalizeInternalLinkValue(typeof attrs.mentionType === "string" ? attrs.mentionType : "");
    if (!id || !mentionType)
        return null;
    const versionIntent = parseMentionVersionRef(typeof attrs.versionRef === "string" ? attrs.versionRef : null);
    return versionIntent ? { id, mentionType, versionIntent } : { id, mentionType };
}
export function parseMessageMentionHref(href) {
    if (!href)
        return null;
    try {
        const parsed = parseInternalHref(href);
        if (!parsed)
            return null;
        if (parsed.pathname !== MESSAGE_MENTION_PATH) {
            return null;
        }
        const id = normalizeInternalLinkValue(parsed.searchParams.get("id"));
        const mentionType = normalizeInternalLinkValue(parsed.searchParams.get("type"));
        if (!id || !mentionType)
            return null;
        const versionNumber = parsePositiveInteger(parsed.searchParams.get(MENTION_VERSION_PARAM));
        if (versionNumber) {
            return { id, mentionType, versionIntent: { kind: "preview", versionNumber } };
        }
        const diffMatch = /^(\d+)-(\d+)$/.exec(parsed.searchParams.get(MENTION_DIFF_PARAM) ?? "");
        const diffFrom = parsePositiveInteger(diffMatch?.[1]);
        const diffTo = parsePositiveInteger(diffMatch?.[2]);
        if (diffFrom && diffTo) {
            const versionIntent = normalizeVersionIntent({
                kind: "diff",
                fromVersionNumber: diffFrom,
                toVersionNumber: diffTo,
            });
            if (versionIntent)
                return { id, mentionType, versionIntent };
        }
        return { id, mentionType };
    }
    catch {
        return null;
    }
}
/**
 * The legacy runtime annotation for a mention: `@Label [df-mention type=… id=…]`.
 *
 * Nothing produces this form any more. It was the projection the model used to be shown
 * instead of the canonical link, until LAT-895 established one syntax everywhere: what the
 * model reads is what the model is asked to author is what a reader sees. The form survives
 * in messages, compaction summaries and channel replies persisted before the flip, and in
 * replies from a model that imitated its own bracketed history — so every surface that
 * persists, displays or replays text repairs it back to the link form.
 *
 * Capture groups: 1 = label text without its `@` (absent when the annotation stands alone),
 * 2 = the horizontal whitespace between the preceding prose and the annotation, 3 = mention
 * type, 4 = mention id. The label sits on the same line as the annotation and begins at the
 * *last* place a mention could start before it: an `@` that is not glued to a preceding word
 * character. Inside the label an `@` survives only where it could not open a mention of its
 * own — glued to the word before it (`ops@acme`) or followed by whitespace (`ROAS @ 7d`) — so
 * a plain `@Jane Doe` earlier in the sentence is never swallowed, and a label that legitimately
 * contains an `@` is never split. Square brackets are ordinary label characters
 * (`Revenue [draft]`), except that `[df-mention` is always the annotation itself and `](` is
 * always the close of a markdown link, so an already-canonical `[@Label](…)` on the same line
 * is never folded into a later bare annotation's label.
 */
export const MESSAGE_MENTION_ANNOTATION_PATTERN = /(?:(?<![\w@])@((?:[^@[\]\r\n]|\[(?!df-mention)|\](?!\()|(?<=\S)@|@(?=\s))*?\S))?([^\S\r\n]*)\[df-mention\s+type=([a-zA-Z0-9_-]+)\s+id=([^\]\s]+)\]/g;
/**
 * Rewrites every legacy annotation into canonical mention markdown, reporting how many it
 * had to repair so callers can log the defect. A label-less annotation falls back to the
 * renderer's own convention of using the id as the label, so the chip still resolves.
 */
export function repairMessageMentionAnnotations(value) {
    if (!value.includes("[df-mention")) {
        return { text: value, repairedCount: 0 };
    }
    let repairedCount = 0;
    const text = value.replaceAll(MESSAGE_MENTION_ANNOTATION_PATTERN, (_match, label, gap, mentionType, id) => {
        repairedCount += 1;
        const token = serializeMessageMentionToken({
            id,
            mentionType,
            label: label ? `@${label}` : id,
        });
        // A labelled annotation consumed its own label, so the gap between them goes with it;
        // a bare one is spliced into surrounding prose and keeps the spacing it was found with.
        return label ? token : `${gap}${token}`;
    });
    return { text, repairedCount };
}
/** {@link repairMessageMentionAnnotations} for callers that do not need the defect count. */
export function canonicalizeMessageMentionAnnotations(value) {
    return repairMessageMentionAnnotations(value).text;
}
/**
 * Reduces every legacy annotation to the text a reader would have seen — its label, or
 * nothing at all when it carried none. Surfaces that cannot render a chip want this rather
 * than {@link canonicalizeMessageMentionAnnotations}: a label-less annotation has no readable
 * text, and inventing one from the id would put a raw identifier in front of a human.
 */
export function degradeMessageMentionAnnotationsToLabels(value) {
    if (!value.includes("[df-mention")) {
        return value;
    }
    return value.replaceAll(MESSAGE_MENTION_ANNOTATION_PATTERN, (_match, label) => (label ? `@${label}` : ""));
}
